import { NgComponentOutlet } from '@angular/common';
import {
  afterNextRender,
  Component,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  viewChild,
} from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { IonNav, ModalController } from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import { BreakpointService, HapticsService } from '@marineform/fe-util';
import {
  manageAbsolutePath,
  MODULE_ACTIONS,
  MODULE_DESCRIPTOR,
  MODULE_STORE,
  NESTED_RECORD_TITLE,
} from '@marineform/manage-data-access';
import { RecordMenuService } from '@marineform/manage-feature';
import {
  SscButtonComponent,
  SscButtonHierarchy,
  SscButtonSize,
  SscIconComponent,
  SscIconHierarchy,
} from '@marineform/ssc-ui';
@Component({
  selector: 'app-record-root',
  template: `
    <ion-header class="ion-no-border record-content-header mg-header-fade">
      <ion-toolbar>
        <ion-title size="large">
          <ng-container
            [ngComponentOutlet]="recordTitleComponent"></ng-container>
        </ion-title>

        @if (isTablet()) {
          <ion-buttons slot="start">
            <button
              (click)="close()"
              ssc-button
              [size]="buttonSize.small"
              [hierarchy]="buttonHierarchy.inverted">
              <ssc-icon
                small
                [hierarchy]="iconHierarchy.basic"
                [svg]="'x'" />
            </button>
          </ion-buttons>
        }
        @if (!isNewRecord() && !is404()) {
          <ion-buttons slot="end" class="self-center">
            <button
              (click)="openMenu()"
              ssc-button
              [size]="buttonSize.small"
              [hierarchy]="buttonHierarchy.inverted">
              <ssc-icon
                small
                [hierarchy]="iconHierarchy.basic"
                [svg]="'dots-three-outline'" />
            </button>
          </ion-buttons>
        }
      </ion-toolbar>
    </ion-header>
    <ion-content
      #recordContent
      [fullscreen]="true"
      class="ion-padding record-page-padding">
      <ng-container
        [ngComponentOutlet]="recordComponent"
        [ngComponentOutletInjector]="recordOutletInjector"
        [ngComponentOutletInputs]="{
          id: id(),
          onCreate: onCreate,
        }"></ng-container>
    </ion-content>
  `,
  imports: [
    NgComponentOutlet,
    IonicModule,
    SscButtonComponent,
    SscIconComponent,
  ],
})
export class RecordRootComponent<T> {
  buttonSize = SscButtonSize;
  #bp = inject(BreakpointService);
  #haptics = inject(HapticsService);
  isTablet = this.#bp.isTablet;
  ionNav = input<IonNav>();
  id = model<number | undefined>();
  private readonly contentHost = viewChild.required<
    unknown,
    ElementRef<IonContentLayoutHost>
  >('recordContent', { read: ElementRef });
  readonly #injector = inject(Injector);
  #cachedOutletInjector?: Injector;

  constructor() {
    afterNextRender(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          void this.contentHost().nativeElement.recalculateDimensions();
        });
      });
    });
  }

  /** Injected into modal + dynamic outlets; Ionic modal does not inherit route tokens without explicit providers. */
  get recordOutletInjector(): Injector {
    if (!this.#cachedOutletInjector) {
      const descriptor = this.#descriptor;
      this.#cachedOutletInjector = Injector.create({
        parent: this.#injector,
        providers: [
          { provide: IonNav, useValue: this.ionNav() },
          { provide: MODULE_DESCRIPTOR, useValue: descriptor },
          { provide: MODULE_STORE, useValue: this.#store },
          { provide: MODULE_ACTIONS, useValue: this.recordActions },
          {
            provide: NESTED_RECORD_TITLE,
            useFactory: (injector: Injector) => ({
              component: descriptor.recordTitleComponent,
              injector,
            }),
            deps: [Injector],
          },
        ],
      });
    }
    return this.#cachedOutletInjector;
  }

  #modal = inject(ModalController);
  #router = inject(Router);
  #recordMenu = inject(RecordMenuService);
  #descriptor = inject(MODULE_DESCRIPTOR);
  recordComponent = this.#descriptor.recordComponent;
  recordTitleComponent = this.#descriptor.recordTitleComponent;
  recordActions = inject(MODULE_ACTIONS);
  #store = inject(MODULE_STORE);
  record = this.#store.record;
  isLoading = this.#store.isRecordLoading;
  isNewRecord = this.#store.isRecordNew;
  is404 = this.#store.is404;
  buttonHierarchy = SscButtonHierarchy;
  iconHierarchy = SscIconHierarchy;
  onCreate = (id: number): void => {
    this.id.set(id);
  };
  async close() {
    const topModal = await this.#modal.getTop();
    if (topModal) {
      await topModal.dismiss();
      return;
    }

    void this.#router.navigateByUrl(
      manageAbsolutePath(this.#descriptor.manageModule.path),
    );
  }

  async openMenu() {
    this.#haptics.mediumImpact();
    await this.#recordMenu.open({
      injector: this.recordOutletInjector,
      nav: this.ionNav(),
      onRecordGone: () => {
        if (this.id() != null) {
          void this.close();
        }
      },
    });
  }
}

/** Stencil `ion-content` — recalculateDimensions is not proxied on Angular's IonContent. */
interface IonContentLayoutHost extends HTMLElement {
  recalculateDimensions(): Promise<void>;
}
